import { useState, useEffect } from "react";
import { Select, Form, Button, Upload, message, Modal, Table } from "antd";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { UploadOutlined, CheckCircleTwoTone } from "@ant-design/icons";

import "./DlUpload.scss";
import { excelErrorList, DLCreationErrorList } from "./options";
import {
  UploadExcelDataFormat,
  ExcelErrorFormat,
  FormattedUploadExcelDataFormat,
} from "./types";
import { checkExcelData } from "./utils/checkExcelData";

import { MenuProps } from "../businessManagement/types";
import { header } from "../utils/salesAdminHeader";

import ContentTitle from "../../../components/ui/ContentTitle";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";

//Permission usage
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

interface MediaIdOptionsType {
  localMarketingId?: number;
  categoryId?: number;
  code: string;
  name: string;
}

const DlUpload = ({ setSelectedService, setSelectSubItem }: MenuProps) => {
  dayjs.extend(isSameOrBefore);
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState("");
  const [selectedMedia, setSelectedMedia] = useState("");
  const [mediaList, setMediaList] = useState<MediaIdOptionsType[]>();
  const [uploadedData, setUploadedData] = useState<
    UploadExcelDataFormat[] | any
  >([]);
  const [mediaFullList, setMediaFullList] = useState({
    CL: [],
    DLBD: [],
    DLGD: [],
    DLLD: [],
  });
  const [dataErrorList, setDataErrorList] = useState<ExcelErrorFormat[]>([]);
  const [modalOpen, setModalOpen] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdData, setCreatedData] = useState<any>([]);

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target?.result) {
        message.error("Fail to upload file!");
        return;
      }

      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      let jsonData = XLSX.utils.sheet_to_json(sheet);

      const columnMapping: Record<string, string> = {
        미디어코드: "MEDIA",
        이름: "KNAME",
        휴대폰번호: "MBPHONE",
        우편번호: "ZIP",
        주소1: "Address1",
        자녀1: "CNAME1",
        "자녀1 생일": "CBIRTH1",
        주소2: "Address2",
        "시/군/구 코드": "SGG_CD",
        "도로명 코드": "ROAD_CD",
        "법정동/법정리 코드": "EMD_CD",
        건물관리번호: "BULD_NO",
        관계: "ParentRelation",
        메모: "MEMO",
      };

      let hasError = false;

      jsonData = jsonData.map((row: any, index: number) => {
        const newRow: Record<string, any> = {};

        Object.keys(row).forEach((key) => {
          const newKey = columnMapping[key] || key;
          newRow[newKey] = row[key];
        });

        const phone = newRow.MBPHONE?.toString().replace(/\s/g, "");
        const phoneRegex1 = /^010\d{8}$/;
        const phoneRegex2 = /^010-\d{4}-\d{4}$/;

        if (!phoneRegex1.test(phone) && !phoneRegex2.test(phone)) {
          hasError = true;
          message.error(`의 휴대폰번호 형식이 올바르지 않습니다: ${phone}`);
        }

        if (newRow.CLDL !== "DL") {
          hasError = true;
          message.error(
            `${index + 2} 행의 CLDL 값이 'DL'이 아닙니다: ${newRow.CLDL}`
          );
        }

        const lidno = String(newRow.LIDNO || "").trim();
        if (!lidno || lidno.length !== 6) {
          hasError = true;
          message.error(
            `${
              index + 2
            } 행의 LIDNO 값이 비어 있거나 6자리가 아닙니다: "${lidno}"`
          );
        }

        if (newRow.CBIRTH1) {
          if (typeof newRow.CBIRTH1 === "number" && newRow.CBIRTH1 > 30000) {
            const date = XLSX.SSF.parse_date_code(newRow.CBIRTH1);
            newRow.CBIRTH1 = `${date.y}-${String(date.m).padStart(
              2,
              "0"
            )}-${String(date.d).padStart(2, "0")}`;
          } else if (typeof newRow.CBIRTH1 === "string") {
            const parsedDate = dayjs(newRow.CBIRTH1, [
              "YYYY-MM-DD",
              "YYYY/MM/DD",
              "MM-DD-YYYY",
              "DD/MM/YYYY",
            ]);

            if (parsedDate.isValid()) {
              newRow.CBIRTH1 = parsedDate.format("YYYY-MM-DD");
            }
          }
        }

        if (newRow.DueDate) {
          if (typeof newRow.DueDate === "number" && newRow.DueDate > 30000) {
            const date = XLSX.SSF.parse_date_code(newRow.DueDate);
            newRow.DueDate = `${date.y}-${String(date.m).padStart(
              2,
              "0"
            )}-${String(date.d).padStart(2, "0")}`;
          } else if (typeof newRow.DueDate === "string") {
            const parsedDate = dayjs(newRow.DueDate, [
              "YYYY-MM-DD",
              "YYYY/MM/DD",
              "MM-DD-YYYY",
              "DD/MM/YYYY",
            ]);

            if (parsedDate.isValid()) {
              newRow.DueDate = parsedDate.format("YYYY-MM-DD");
            }
          }
        }

        if (newRow.Address2 !== undefined && newRow.Address2 !== null) {
          newRow.Address2 = String(newRow.Address2);
        }

        return newRow;
      });

      if (hasError) {
        return;
      }

      setUploadedData(jsonData);
    };

    reader.readAsBinaryString(file);
    return false;
  };

  const resetToStart = () => {
    setIsSuccess(false);
    setSelectedMedia("");
    setUploadedData([]);
    setSelectedType("");
    form.setFieldsValue({
      type: undefined,
      media: undefined,
    });
  };

  const handleCreate = async (
    formattedData: FormattedUploadExcelDataFormat
  ) => {
    const data = await apiCall({
      method: "POST",
      header: header,
      endPoint: process.env.REACT_APP_SALES_ADMIN + "/leads/excel/DL",
      payload: { data: formattedData },
    });
    if (data) {
      if (data.response?.status === 400) {
        message.error(
          `행 ${data.response.data[0].index + 2} ${
            DLCreationErrorList.find(
              (i) => i.label === data.response.data[0].status
            )?.value
          }`
        );
      } else {
        setIsSuccess(true);
        setCreatedData(data);
      }
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    if (createdData.length > 0) {
      const wsCreated = XLSX.utils.json_to_sheet(createdData);
      XLSX.utils.book_append_sheet(wb, wsCreated, "Created Data");
    }

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    FileSaver.saveAs(
      data,
      `DLUploadResult-${dayjs().format("YYYY-MM-DD")}.xlsx`
    );
  };

  const fetchMedia = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + "/leads/admin/options/media",
    });
    if (data) setMediaFullList(data);
  };

  const errorColumns = [
    { title: "행", dataIndex: "row", key: "row" },
    {
      title: "오류",
      dataIndex: "error",
      key: "error",
      render: (type: string) =>
        excelErrorList.find((item) => item.label === type)?.value,
    },
  ];

  useEffect(() => {
    fetchMedia();
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("리드 관리");
  }, []);

  useEffect(() => {
    if (uploadedData.length > 0) {
      if (checkExcelData(uploadedData, true).isPass) {
        const today = dayjs().format("YYYY-MM-DD");
        const payload = uploadedData.map((lead: UploadExcelDataFormat) => {
          const formatPhoneNumber = (phone: string) => {
            if (phone.length === 11 && !phone.includes("-")) {
              return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(
                7
              )}`;
            }
            return phone;
          };
          const requiredFields = {
            cldlType: "DL",
            leadType: selectedType,
            name: lead.KNAME,
            mobileNumber: formatPhoneNumber(lead.MBPHONE),
            zipCode: lead.ZIP.toString(),
            address1: lead.Address1,
            parentRelation: lead.ParentRelation,
            companyNumber: lead.LIDNO,
            ...(selectedType === "GD"
              ? { localMarketingId: selectedMedia }
              : { categoryId: selectedMedia }),
          };
          const optionalFields = {
            buildingNumber: lead.BULD_NO,
            emdCode: lead.EMD_CD,
            roadCode: lead.ROAD_CD,
            sggCode: lead.SGG_CD,
            address2: lead.Address2,
            childGender:
              lead.C1IsMan === undefined || lead.C1IsMan === null
                ? null
                : Boolean(lead.C1IsMan)
                ? "male"
                : "female",
            memo: lead.MEMO,
            childName: lead.CNAME1,
            childBirthDate:
              lead.CBIRTH1 && dayjs(lead.CBIRTH1).isSameOrBefore(dayjs(today))
                ? lead.CBIRTH1
                : "",
            dueDate: lead.DueDate
              ? lead.DueDate
              : lead.CBIRTH1 && dayjs(lead.CBIRTH1).isAfter(dayjs(today))
              ? lead.CBIRTH1
              : "",
            isPregnant:
              lead.CBIRTH1 && dayjs(lead.CBIRTH1).isAfter(dayjs(today))
                ? true
                : lead.IsPregnant !== "" &&
                  lead.IsPregnant !== null &&
                  lead.IsPregnant !== undefined
                ? Boolean(lead.IsPregnant)
                : null,
          };
          const finalData = Object.entries(optionalFields).reduce(
            (acc, [key, val]) =>
              val !== "" && val !== undefined && val !== null
                ? { ...acc, [key]: val }
                : acc,
            { ...requiredFields }
          );
          return finalData;
        });

        handleCreate(payload);
      } else {
        setDataErrorList(checkExcelData(uploadedData, true).errorList);
        setModalOpen(1);
      }
    }
  }, [uploadedData]);

  useEffect(() => {
    const selectedList =
      selectedType === "GD"
        ? mediaFullList.DLGD
        : selectedType === "LD"
        ? mediaFullList.DLLD
        : mediaFullList.DLBD;
    setMediaList(selectedList);
  }, [selectedType, mediaFullList]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Lead Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="dlUploadWrapper">
      <ContentTitle title="DL Upload" />
      <div className="content-container">
        <Form form={form} layout="inline">
          <Form.Item
            rules={[requiredRule]}
            label="Type"
            className="w-1/5"
            name="type"
          >
            <Select
              onChange={(e) => {
                setSelectedType(e);
                setSelectedMedia("");
                form.setFieldsValue({ media: undefined });
              }}
              options={[
                { label: "LD", value: "LD" },
                { label: "BD", value: "BD" },
                { label: "GD", value: "GD" },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Media"
            rules={[requiredRule]}
            name="media"
            className="w-2/5"
          >
            <Select
              disabled={!selectedType}
              onChange={(e) => {
                setSelectedMedia(e);
              }}
              options={mediaList?.map((i) => {
                return {
                  label: i.name,
                  value: i.categoryId || i.localMarketingId,
                };
              })}
            />
          </Form.Item>
          <Upload
            beforeUpload={handleFileUpload}
            showUploadList={false}
            disabled={!selectedType || !selectedMedia}
          >
            <Button
              disabled={!selectedMedia || !selectedType}
              icon={<UploadOutlined />}
            >
              엑셀 업로드
            </Button>
          </Upload>
        </Form>
        {isSuccess && (
          <div>
            <div className="flex justify-center">
              <CheckCircleTwoTone
                twoToneColor="#00B140"
                style={{
                  fontSize: "80px",
                  marginTop: 40,
                  marginBottom: 30,
                }}
              />
            </div>
            <div>
              <p className="text-center text-2xl">
                리드등록 및 배분이 완료되었습니다.
              </p>
              <p className="text-center text-sm">
                배분된 리드 목록을 다운로드하여 엑셀에서 확인하세요.
              </p>
              <p className="text-center text-sm mt-1">
                이 페이지를 벗어나면 다시 목록을 받을 수 없습니다.
              </p>
            </div>
            <div className="flex justify-center mt-5">
              <Button type="primary" onClick={exportToExcel}>
                리드배분 목록 다운로드
              </Button>
              <Button onClick={resetToStart} className="ml-5">
                확인
              </Button>
            </div>
          </div>
        )}
      </div>
      <Modal
        title={
          <div className="flex">
            <img
              alt="wrong circle icon"
              src={require("../../../assets/icons/close-circle.png")}
              style={{ width: 22, height: 22, marginRight: 16 }}
            />
            <div>
              <p className="text-base font-normal">
                데이터가 유효하지 않습니다.
              </p>
              <p className="text-sm font-normal mt-1">엑셀 파일 다시 업로드</p>
            </div>
          </div>
        }
        open={modalOpen === 1}
        closable={false}
        footer={
          <div className="flex justify-end mt-5">
            <Button
              className="me-3"
              onClick={() => {
                setModalOpen(0);
                setDataErrorList([]);
              }}
            >
              취소
            </Button>
            <Upload beforeUpload={handleFileUpload} showUploadList={false}>
              <Button type="primary" danger>
                엑셀 파일 다시 업로드
              </Button>
            </Upload>
          </div>
        }
      >
        <Table
          columns={errorColumns}
          dataSource={dataErrorList}
          size="small"
          scroll={{ x: "max-content", y: 236 }}
          pagination={false}
        />
      </Modal>
    </div>
  );
};

export default DlUpload;
